import { useMemo } from 'react';
import { isCpoProperty, isEligibleCpControllingProperty, isEligibleCpoProperty } from '../../conditionalLogic/utils';
import { useAllPropertyValues } from './useAllPropertyValues';
import { useConditionalPropertyOptionsRulesForObjectType } from './useConditionalPropertyOptionsRulesForObjectType';
import { useConditionalPropertyRulesForObjectType } from './useConditionalPropertyRulesForObjectType';
import { useProperties } from './useProperties';
const emptyPropertyMap = {};
const emptyConditionalPropertyRulesArray = [];
const emptyConditionalPropertyOptionsRulesArray = [];
export const useConditionalPropertiesPanelData = ({
  objectTypeId,
  property,
  objectId,
  providedConditionalPropertyRules,
  enableConditionalPropertiesPanel,
  hasDependentAssociationsAccess
}) => {
  const skipCpRules = !enableConditionalPropertiesPanel || !isEligibleCpControllingProperty(property) || !!providedConditionalPropertyRules;
  const skipCpoRules = !enableConditionalPropertiesPanel || !isEligibleCpoProperty({
    property,
    objectTypeId
  });
  const {
    data: fetchedConditionalPropertyRules,
    loading: conditionalPropertyRulesLoading,
    error: conditionalPropertyRulesError
  } = useConditionalPropertyRulesForObjectType(objectTypeId, {
    skip: skipCpRules
  }, hasDependentAssociationsAccess);
  const {
    data: conditionalPropertyOptionsRules,
    loading: conditionalPropertyOptionsRulesLoading,
    error: conditionalPropertyOptionsRulesError
  } = useConditionalPropertyOptionsRulesForObjectType(objectTypeId, {
    skip: skipCpoRules
  });

  // The data requirements for CPO can be very heavy, so we prefer to skip whenever possible!
  // Historically, overfetching here causes real pain on the associations cards on the records page,
  // for example. Fetching all properties and values for an associated object type is undesirable unless
  // necessary to confirm data quality around property edits that might have a CPO rule.
  const skipCpoData = useMemo(() => !isCpoProperty(conditionalPropertyOptionsRules || [], property), [conditionalPropertyOptionsRules, property]);
  const {
    data: allPropertiesForObjectType,
    loading: allPropertiesLoading,
    error: allPropertiesError
  } = useProperties(objectTypeId, {
    skip: skipCpoData
  });
  const {
    data: allPropertyValues,
    loading: allPropertyValuesLoading,
    error: allPropertyValuesError
  } = useAllPropertyValues({
    objectId,
    objectTypeId,
    skip: skipCpoData
  });
  const conditionalPropertyOptionsRulesForProperty = useMemo(() => {
    return (conditionalPropertyOptionsRules || []).filter(rule => rule.controllingField.fieldName === property.name);
  }, [conditionalPropertyOptionsRules, property.name]);
  const loading = conditionalPropertyRulesLoading || conditionalPropertyOptionsRulesLoading || allPropertiesLoading || allPropertyValuesLoading;
  const error = conditionalPropertyRulesError || conditionalPropertyOptionsRulesError || allPropertiesError || allPropertyValuesError;
  return useMemo(() => ({
    conditionalPropertyRulesForObjectType: providedConditionalPropertyRules || fetchedConditionalPropertyRules || emptyConditionalPropertyRulesArray,
    conditionalPropertyOptionsRules: conditionalPropertyOptionsRules || emptyConditionalPropertyOptionsRulesArray,
    conditionalPropertyOptionsRulesForProperty,
    allPropertiesForObjectType: allPropertiesForObjectType || emptyPropertyMap,
    allPropertyValues,
    loading,
    error
  }), [providedConditionalPropertyRules, fetchedConditionalPropertyRules, conditionalPropertyOptionsRules, conditionalPropertyOptionsRulesForProperty, allPropertiesForObjectType, allPropertyValues, loading, error]);
};